// data-fit LLC · DecisionFlow · TenantAdmin Stage
// AccessScopeConfig.jsx · DDL: R13 · API: v1.22 · Session 159
// Auth: useAuth() via AuthContext — ARCH-2 compliant
// Key constraints: ER-1 limits from API only; enterprise scope gated by JWT tier; region/sub_region XOR enforced
// Stage 12 endpoints wired — api_contract v1.15 §35
// M040 C2: dark: prefix removed, hardcoded colors → tokens, text-* → text-tok-*

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Ban, MapPin, Package, Settings2, X } from 'lucide-react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { apiGet, apiPost, apiDelete } from '../../api/client.js'
import Button from '../shared/Button.jsx'
import Card from '../shared/Card.jsx'
import Field from '../shared/Field.jsx'
import Pill from '../shared/Pill.jsx'
import { SCREEN_ORDER } from './TenantAdminShell'

const SUBSCRIPTION_TIERS = {
  ESSENTIALS: 'essentials',
  STANDARD:   'standard',
  ENTERPRISE: 'enterprise',
}

const SCOPE_NODE_TYPES = {
  REGION:     'region',
  SUB_REGION: 'sub_region',
}

const EMPTY_CONFIG_FORM = {
  max_positions:    '',
  max_comp_admins:  '',
  max_finance_users: '',
}

const EMPTY_SCOPE_FORM = {
  user_account_id: '',
  node_type:       SCOPE_NODE_TYPES.REGION,
  region_id:       '',
  sub_region_id:   '',
}

const ERROR_MESSAGES = {
  AUTH_INSUFFICIENT_ROLE:
    'You do not have sufficient permissions to manage tenant configuration.',
  SCOPE_XOR_VIOLATION:
    'Scope assignment requires exactly one geo node: either region_id or sub_region_id, not both.',
  GENERIC: 'Unable to complete the access scope request.',
}

// ---------------------------------------------------------------------------
// Shared input classes — M040 tokens
// ---------------------------------------------------------------------------

const INPUT_CLS = `w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm
  text-[rgb(var(--color-text))] placeholder-[rgb(var(--color-text-muted))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))]/20
  disabled:opacity-50`

const SELECT_CLS = `w-full rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] px-3 py-2 text-tok-sm
  text-[rgb(var(--color-text))] focus:outline-none focus:ring-2 focus:ring-[rgb(var(--color-primary))]/20
  disabled:opacity-50`

const LABEL_CLS = `block text-tok-xs font-medium text-[rgb(var(--color-text-muted))] mb-1`

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseApiError(error) {
  if (error && typeof error === 'object') {
    const { code, message } = error
    return { code: code || 'UNKNOWN_ERROR', message: message || ERROR_MESSAGES.GENERIC }
  }
  return { code: 'UNKNOWN_ERROR', message: ERROR_MESSAGES.GENERIC }
}

function displayLimit(value) {
  if (value === null || value === undefined || value === '') return 'Unlimited'
  return String(value)
}

function formValueFromConfig(value) {
  if (value === null || value === undefined) return ''
  return String(value)
}

function parseNullableInteger(value) {
  if (value === null || value === undefined || String(value).trim() === '') return null
  return Number(value)
}

function buildScopeQuery({ cursor, selectedUser }) {
  const params = new URLSearchParams()
  params.set('limit', '50')
  if (cursor) params.set('cursor', cursor)
  if (selectedUser) params.set('user_account_id', selectedUser)
  return params.toString()
}

// ---------------------------------------------------------------------------
// AccessScopeConfig
// ---------------------------------------------------------------------------

export default function AccessScopeConfig({
  aiStatus,
  aiResponse,
  currentScreen,
  onNavigate,
}) {
  const { user } = useAuth()

  const [config, setConfig]         = useState(null)
  const [configForm, setConfigForm] = useState(EMPTY_CONFIG_FORM)

  const [scopes, setScopes]             = useState([])
  const [selectedUser, setSelectedUser] = useState('')
  const [scopeForm, setScopeForm]       = useState(EMPTY_SCOPE_FORM)

  const [nextCursor, setNextCursor]       = useState(null)
  const [hasMore, setHasMore]             = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)

  const [confirmAction, setConfirmAction] = useState(null)

  const [error, setError]                   = useState(null)
  const [successMessage, setSuccessMessage] = useState(null)
  const [loadingConfig, setLoadingConfig]   = useState(false)
  const [loadingScopes, setLoadingScopes]   = useState(false)
  const [saving, setSaving]                 = useState(false)

  const isCurrentScreen  = currentScreen === SCREEN_ORDER[2]
  const isEnterpriseTier = user?.tier === SUBSCRIPTION_TIERS.ENTERPRISE

  // ---------------------------------------------------------------------------
  // Load tenant config
  // ---------------------------------------------------------------------------

  const loadTenantConfig = useCallback(async () => {
    setLoadingConfig(true)
    setError(null)

    try {
      const payload = await apiGet('/api/v1/tenant-config')

      setConfig(payload)
      setConfigForm({
        max_positions:     formValueFromConfig(payload.max_positions),
        max_comp_admins:   formValueFromConfig(payload.max_comp_admins),
        max_finance_users: formValueFromConfig(payload.max_finance_users),
      })
    } catch (caughtError) {
      const { code, message } = parseApiError(caughtError)
      setError(code === 'AUTH_INSUFFICIENT_ROLE' ? ERROR_MESSAGES.AUTH_INSUFFICIENT_ROLE : message)
    } finally {
      setLoadingConfig(false)
    }
  }, [])

  // ---------------------------------------------------------------------------
  // Load scopes
  // ---------------------------------------------------------------------------

  const loadScopes = useCallback(
    async ({ cursor = null, targetUser = '', append = false } = {}) => {
      if (!targetUser) return
      if (append) setIsLoadingMore(true)
      else setLoadingScopes(true)
      setError(null)

      try {
        const queryString = buildScopeQuery({ cursor, selectedUser: targetUser })
        const payload = await apiGet(`/api/v1/user-access-scopes?${queryString}`)

        const data = Array.isArray(payload?.data) ? payload.data : []
        const pagination = payload?.pagination || {}
        setScopes((prev) => (append ? [...prev, ...data] : data))
        setNextCursor(pagination.next_cursor || null)
        setHasMore(Boolean(pagination.has_more))
      } catch (caughtError) {
        const { code, message } = parseApiError(caughtError)
        setError(code === 'AUTH_INSUFFICIENT_ROLE' ? ERROR_MESSAGES.AUTH_INSUFFICIENT_ROLE : message)
      } finally {
        setLoadingScopes(false)
        setIsLoadingMore(false)
      }
    },
    [],
  )

  useEffect(() => {
    if (!isCurrentScreen) return
    loadTenantConfig()
  }, [isCurrentScreen, loadTenantConfig])

  useEffect(() => {
    if (!isCurrentScreen || !isEnterpriseTier) return
    if (!selectedUser) {
      setScopes([])
      setNextCursor(null)
      setHasMore(false)
      return
    }
    loadScopes({ cursor: null, targetUser: selectedUser, append: false })
  }, [isCurrentScreen, isEnterpriseTier, loadScopes, selectedUser])

  // ---------------------------------------------------------------------------
  // Config form
  // ---------------------------------------------------------------------------

  function updateConfigForm(fieldName, value) {
    setConfigForm((current) => ({ ...current, [fieldName]: value }))
  }

  async function patchTenantConfig() {
    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const body = {
        max_positions:     parseNullableInteger(configForm.max_positions),
        max_comp_admins:   parseNullableInteger(configForm.max_comp_admins),
        max_finance_users: parseNullableInteger(configForm.max_finance_users),
      }

      const payload = await fetch('/api/v1/tenant-config', {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      }).then((r) => r.json())

      const nextConfig = {
        ...config,
        ...payload,
        max_positions:
          payload.max_positions !== undefined ? payload.max_positions : body.max_positions,
        max_comp_admins:
          payload.max_comp_admins !== undefined ? payload.max_comp_admins : body.max_comp_admins,
        max_finance_users:
          payload.max_finance_users !== undefined ? payload.max_finance_users : body.max_finance_users,
      }

      setConfig(nextConfig)
      setConfigForm({
        max_positions:     formValueFromConfig(nextConfig.max_positions),
        max_comp_admins:   formValueFromConfig(nextConfig.max_comp_admins),
        max_finance_users: formValueFromConfig(nextConfig.max_finance_users),
      })
      setSuccessMessage('Tenant configuration updated.')
    } catch (caughtError) {
      const { code, message } = parseApiError(caughtError)
      setError(code === 'AUTH_INSUFFICIENT_ROLE' ? ERROR_MESSAGES.AUTH_INSUFFICIENT_ROLE : message)
    } finally {
      setSaving(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Scope form
  // ---------------------------------------------------------------------------

  function updateSelectedUser(value) {
    setSelectedUser(value.trim())
    setScopeForm((current) => ({ ...current, user_account_id: value.trim() }))
    setSuccessMessage(null)
  }

  function updateScopeForm(fieldName, value) {
    setScopeForm((current) => {
      if (fieldName === 'node_type') {
        return { ...current, node_type: value, region_id: '', sub_region_id: '' }
      }
      if (fieldName === 'region_id') {
        return { ...current, region_id: value, sub_region_id: '', node_type: SCOPE_NODE_TYPES.REGION }
      }
      if (fieldName === 'sub_region_id') {
        return { ...current, sub_region_id: value, region_id: '', node_type: SCOPE_NODE_TYPES.SUB_REGION }
      }
      return { ...current, [fieldName]: value }
    })
  }

  function validateScopeXor() {
    const hasRegion    = String(scopeForm.region_id    || '').trim().length > 0
    const hasSubRegion = String(scopeForm.sub_region_id || '').trim().length > 0
    return (hasRegion && !hasSubRegion) || (!hasRegion && hasSubRegion)
  }

  async function assignScope() {
    setError(null)
    setSuccessMessage(null)

    if (!validateScopeXor()) { setError(ERROR_MESSAGES.SCOPE_XOR_VIOLATION); return }

    setSaving(true)

    try {
      const body = { user_account_id: scopeForm.user_account_id.trim() }
      if (String(scopeForm.region_id    || '').trim()) body.region_id     = Number(scopeForm.region_id)
      if (String(scopeForm.sub_region_id || '').trim()) body.sub_region_id = Number(scopeForm.sub_region_id)

      const payload = await apiPost('/api/v1/user-access-scopes', body)

      setScopes((current) => [payload, ...current])
      setScopeForm({ ...EMPTY_SCOPE_FORM, user_account_id: scopeForm.user_account_id.trim() })
      setSuccessMessage('Access scope assigned.')
    } catch (caughtError) {
      const { code, message } = parseApiError(caughtError)
      if (code === 'SCOPE_XOR_VIOLATION') setError(ERROR_MESSAGES.SCOPE_XOR_VIOLATION)
      else if (code === 'AUTH_INSUFFICIENT_ROLE') setError(ERROR_MESSAGES.AUTH_INSUFFICIENT_ROLE)
      else setError(message)
    } finally {
      setSaving(false)
    }
  }

  function requestRevokeScope(scope) {
    setError(null)
    setSuccessMessage(null)
    setConfirmAction({
      type: 'revoke-scope',
      scope,
      message: 'Revoke this access scope? This is a soft revoke and the row remains visible.',
    })
  }

  async function revokeScope() {
    if (!confirmAction?.scope?.scope_id) return
    const targetScope = confirmAction.scope

    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const payload = await apiDelete(`/api/v1/user-access-scopes/${targetScope.scope_id}`)

      const revokedAt = payload?.revoked_at || new Date().toISOString()
      setScopes((current) =>
        current.map((s) =>
          s.scope_id === targetScope.scope_id
            ? { ...s, revoked_at: revokedAt, is_revoked: true }
            : s,
        ),
      )
      setConfirmAction(null)
      setSuccessMessage('Access scope revoked. The row remains visible as revoked.')
    } catch (caughtError) {
      const { code, message } = parseApiError(caughtError)
      if (code === 'SCOPE_XOR_VIOLATION') setError(ERROR_MESSAGES.SCOPE_XOR_VIOLATION)
      else if (code === 'AUTH_INSUFFICIENT_ROLE') setError(ERROR_MESSAGES.AUTH_INSUFFICIENT_ROLE)
      else setError(message)
    } finally {
      setSaving(false)
    }
  }

  const canPatchConfig = useMemo(() => Boolean(config), [config])
  const canAssignScope = useMemo(
    () => scopeForm.user_account_id.trim().length > 0 && validateScopeXor(),
    [scopeForm],
  )

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-4">
      {/* Screen header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">TA-03</p>
          <h2 className="mt-0.5 text-tok-lg font-bold text-[rgb(var(--color-text))]">Access Scope & Tenant Config</h2>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Maintain tenant seat limits from the tenant row and configure enterprise geo access scopes.
          </p>
        </div>
        <Button variant="secondary" onClick={() => onNavigate(SCREEN_ORDER[1])}>
          ← Role Assignments
        </Button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-start gap-2 rounded-xl bg-[rgb(var(--color-danger))]/10 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]" role="alert">
          <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
        </div>
      )}
      {successMessage && (
        <div className="flex items-start gap-2 rounded-xl bg-[rgb(var(--color-success))]/10 px-4 py-3 text-tok-sm text-[rgb(var(--color-success))]" role="status">
          <span className="flex-1">{successMessage}</span>
          <button type="button" onClick={() => setSuccessMessage(null)} className="shrink-0 opacity-60 hover:opacity-100"><X className="h-4 w-4" /></button>
        </div>
      )}

      {/* Confirm revoke scope */}
      {confirmAction && (
        <div className="rounded-2xl border border-[rgb(var(--color-warning))]/40 bg-[rgb(var(--color-warning))]/10 p-4" role="alertdialog" aria-modal="false">
          <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-warning))]">Confirm Revoke</h3>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text))]">{confirmAction.message}</p>
          <div className="mt-3 flex gap-2">
            <Button variant="danger" onClick={revokeScope} disabled={saving}>
              <Ban className="h-4 w-4" />
              {saving ? 'Revoking...' : 'Confirm Revoke'}
            </Button>
            <Button variant="secondary" onClick={() => setConfirmAction(null)} disabled={saving}>
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Tenant configuration */}
      <Card
        title="Tenant Configuration"
        action={<Settings2 className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />}
      >
        <p className="mb-4 text-tok-xs text-[rgb(var(--color-text-muted))]">
          ER-1: seat limits render from the tenant config API only. Blank max values mean Unlimited.
        </p>

        {loadingConfig && (
          <div className="space-y-2">
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton h-8 rounded-xl" />
            ))}
          </div>
        )}

        {!loadingConfig && !config && (
          <p className="py-4 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">Tenant configuration has not loaded.</p>
        )}

        {config && (
          <>
            {/* Live usage summary */}
            <div className="mb-4 grid grid-cols-2 gap-3 rounded-xl bg-[rgb(var(--color-surface-muted))] p-3 sm:grid-cols-4">
              <Field
                label="Subscription tier"
                value={config.subscription_tier || '—'}
              />
              <Field
                label="Positions"
                value={`${config.usage?.positions ?? '—'} / ${displayLimit(config.max_positions)}`}
              />
              <Field
                label="Comp admins"
                value={`${config.usage?.comp_admins ?? '—'} / ${displayLimit(config.max_comp_admins)}`}
              />
              <Field
                label="Finance users"
                value={`${config.usage?.finance_users ?? '—'} / ${displayLimit(config.max_finance_users)}`}
              />
            </div>

            {/* Editable limits */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div>
                <label className={LABEL_CLS}>Max positions</label>
                <input
                  type="number"
                  className={INPUT_CLS}
                  value={configForm.max_positions}
                  onChange={(e) => updateConfigForm('max_positions', e.target.value)}
                  placeholder="Unlimited"
                />
              </div>
              <div>
                <label className={LABEL_CLS}>Max comp admins</label>
                <input
                  type="number"
                  className={INPUT_CLS}
                  value={configForm.max_comp_admins}
                  onChange={(e) => updateConfigForm('max_comp_admins', e.target.value)}
                  placeholder="Unlimited"
                />
              </div>
              <div>
                <label className={LABEL_CLS}>Max finance users</label>
                <input
                  type="number"
                  className={INPUT_CLS}
                  value={configForm.max_finance_users}
                  onChange={(e) => updateConfigForm('max_finance_users', e.target.value)}
                  placeholder="Unlimited"
                />
              </div>
            </div>

            <div className="mt-3">
              <Button onClick={patchTenantConfig} disabled={!canPatchConfig || saving}>
                <Settings2 className="h-4 w-4" />
                {saving ? 'Saving...' : 'Save Tenant Config'}
              </Button>
            </div>
          </>
        )}
      </Card>

      {/* Plan Packages — coming soon */}
      <Card
        title="Plan Packages"
        action={<Package className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />}
      >
        <div className="flex flex-col items-center gap-3 py-6 text-center">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[rgb(var(--color-surface-muted))]">
            <Package className="h-5 w-5 text-[rgb(var(--color-text-muted))]" />
          </div>
          <div>
            <p className="text-tok-sm font-semibold text-[rgb(var(--color-text))]">Coming Soon</p>
            <p className="mt-1 text-tok-xs text-[rgb(var(--color-text-muted))]">
              Plan package discovery and ordering will be available here in a future release.
            </p>
          </div>
        </div>
      </Card>

      {/* Enterprise geo access scope — gated */}
      {isEnterpriseTier && (
        <Card
          title="Enterprise Geo Access Scope"
          action={<MapPin className="h-4 w-4 text-[rgb(var(--color-text-muted))]" />}
        >
          <p className="mb-4 text-tok-xs text-[rgb(var(--color-text-muted))]">
            Assign exactly one geo node per scope: region_id or sub_region_id. Rows are soft revoked and remain visible.
          </p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={LABEL_CLS}>Selected user account ID</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={selectedUser}
                onChange={(e) => updateSelectedUser(e.target.value)}
                placeholder="UUID"
              />
            </div>
            <div>
              <label className={LABEL_CLS}>Scope user account ID</label>
              <input
                type="text"
                className={INPUT_CLS}
                value={scopeForm.user_account_id}
                onChange={(e) => updateScopeForm('user_account_id', e.target.value)}
                placeholder="UUID"
              />
            </div>
            <div>
              <label className={LABEL_CLS}>Geo node type</label>
              <select
                className={SELECT_CLS}
                value={scopeForm.node_type}
                onChange={(e) => updateScopeForm('node_type', e.target.value)}
              >
                <option value={SCOPE_NODE_TYPES.REGION}>Region</option>
                <option value={SCOPE_NODE_TYPES.SUB_REGION}>Sub-region</option>
              </select>
            </div>

            {scopeForm.node_type === SCOPE_NODE_TYPES.REGION && (
              <div>
                <label className={LABEL_CLS}>Region ID</label>
                <input
                  type="number"
                  className={INPUT_CLS}
                  value={scopeForm.region_id}
                  onChange={(e) => updateScopeForm('region_id', e.target.value)}
                  placeholder="Integer"
                />
              </div>
            )}

            {scopeForm.node_type === SCOPE_NODE_TYPES.SUB_REGION && (
              <div>
                <label className={LABEL_CLS}>Sub-region ID</label>
                <input
                  type="number"
                  className={INPUT_CLS}
                  value={scopeForm.sub_region_id}
                  onChange={(e) => updateScopeForm('sub_region_id', e.target.value)}
                  placeholder="Integer"
                />
              </div>
            )}
          </div>

          <div className="mt-3">
            <Button onClick={assignScope} disabled={!canAssignScope || saving}>
              <MapPin className="h-4 w-4" />
              {saving ? 'Assigning...' : 'Assign Scope'}
            </Button>
          </div>

          {/* Assigned scopes table */}
          <div className="mt-6">
            <h3 className="mb-2 text-tok-sm font-semibold text-[rgb(var(--color-text))]">Assigned Scopes</h3>

            {!selectedUser && (
              <p className="py-4 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
                Select a user account ID to load enterprise access scopes.
              </p>
            )}

            {selectedUser && loadingScopes && (
              <div className="space-y-2">
                {[1, 2].map((i) => (
                  <div key={i} className="skeleton h-8 rounded-xl" />
                ))}
              </div>
            )}

            {selectedUser && !loadingScopes && scopes.length === 0 && (
              <p className="py-4 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
                No access scopes found for this user.
              </p>
            )}

            {selectedUser && !loadingScopes && scopes.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-tok-sm">
                  <thead>
                    <tr className="border-b border-[rgb(var(--color-border))]">
                      {['Scope ID', 'User Account', 'Region', 'Sub-region', 'Status', 'Revoked At', 'Actions'].map((h) => (
                        <th key={h} className="pb-2 pr-4 text-left text-tok-xs font-semibold text-[rgb(var(--color-text-muted))] whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[rgb(var(--color-border))]">
                    {scopes.map((scope) => {
                      const isRevoked = Boolean(scope.revoked_at || scope.is_revoked)
                      return (
                        <tr key={scope.scope_id} className={isRevoked ? 'opacity-50' : ''}>
                          <td className="py-2.5 pr-4 font-mono text-tok-xs text-[rgb(var(--color-text))]">{scope.scope_id}</td>
                          <td className="py-2.5 pr-4 font-mono text-tok-xs text-[rgb(var(--color-text))]">{scope.user_account_id || '—'}</td>
                          <td className="py-2.5 pr-4 text-[rgb(var(--color-text))]">{scope.region_id ?? '—'}</td>
                          <td className="py-2.5 pr-4 text-[rgb(var(--color-text))]">{scope.sub_region_id ?? '—'}</td>
                          <td className="py-2.5 pr-4">
                            <Pill tone={isRevoked ? 'Blocked' : 'Complete'}>
                              {isRevoked ? 'Revoked' : 'Active'}
                            </Pill>
                          </td>
                          <td className="py-2.5 pr-4 text-[rgb(var(--color-text-muted))] whitespace-nowrap">{scope.revoked_at || '—'}</td>
                          <td className="py-2.5">
                            <Button
                              variant="ghost"
                              onClick={() => requestRevokeScope(scope)}
                              disabled={saving || isRevoked}
                            >
                              <Ban className="h-3.5 w-3.5" />
                              Revoke
                            </Button>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {hasMore && (
              <div className="mt-4">
                <Button
                  variant="secondary"
                  onClick={() => loadScopes({ cursor: nextCursor, targetUser: selectedUser, append: true })}
                  disabled={isLoadingMore}
                >
                  {isLoadingMore ? 'Loading...' : 'Load More'}
                </Button>
              </div>
            )}
          </div>
        </Card>
      )}
    </div>
  )
}
